import React, {useEffect, useRef, useState} from 'react';
import {Button} from 'primereact/button';
import {Column} from 'primereact/column';
import {DataTable} from 'primereact/datatable';
import {MultiSelect} from 'primereact/multiselect';
import {Toast} from 'primereact/toast';
import {useAppDispatch, useAppSelector} from 'redux/hooks'
import {Paginator} from "primereact/paginator";
import {classNames} from "primereact/utils";
import {Dialog} from "primereact/dialog";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {checkPermissions} from "../../../../redux/permissions/permissionsUtils";
import IVRConfigurationListNav from "../IVRConfigurationListNav";
import IVRConfigurationListDetails from "./IVRConfigurationListDetails";
import IVRConfigurationDialog from "../IVRConfigurationDialog";
import {
    setIVRConfigurationCurrentPage,
    setIVRConfigurationCurrentPageSize,
    setIVRConfigurationDialogVisible,
    setIVRConfigurationErrorFields,
    setSelectedIVRConfigurationIndex,
} from "../../../../redux/actions/actionsIVRConfiguration";
import {
    copyIVRConfiguration,
    deleteIVRConfiguration,
    getIVRConfigurationById,
    getIvrConfigurationsWithPagination,
} from "../../../../redux/api/apiIVRConfiguration";
import {DataSort} from "../../../../types/types";
import {setEditType} from "../../../../redux/actions/actions";

const IVRConfigurationList = () => {
    const dispatch = useAppDispatch();
    const clearFilters = useAppSelector(state => state.IVRConfigurationReducer.clearFilters);
    const ivrConfigurationsListRaw = useAppSelector(
        (state) => state.IVRConfigurationReducer.ivrConfigurationsList
    );
    const ivrConfigurationsList = Array.isArray(ivrConfigurationsListRaw)
        ? ivrConfigurationsListRaw
        : [];
    const pagination = useAppSelector(state => state.IVRConfigurationReducer.pagination);
    const paginationPage = useAppSelector(state => state.IVRConfigurationReducer.paginationPage);
    const paginationSize = useAppSelector(state => state.IVRConfigurationReducer.paginationSize);
    const loader = useAppSelector(state => state.IVRConfigurationReducer.loading);
    const [mobileVisible, setMobileVisible] = useState<boolean>(false);
    const toast = useRef<Toast>(null);
    const [orderBy, setOrderBy] = useState<string | null>(null);
    const [isDescFilter, setIsDescFilter] = useState<boolean>(true);
    const [sortOrder, setSortOrder] = useState<number>(0);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const activeClick = useAppSelector(state => state.UsersListReducer.activeClick);
    const [filterFlowId, setFilterFlowId] = useState<{ key: string; value: string } | null>(null);
    const [filterName, setFilterName] = useState<{ key: string; value: string } | null>(null);
    const [filterIsActive, setFilterIsActive] = useState<{ key: string; value: string }[] | null>(null);
    const [sortData, setSortData] = useState<any>({
        pageNumber: 1,
        pageSize: 10,
        sort: {
            orderBy: "FlowId",
            isDesc: true,
        },
    });
    const [paginatorTotalRecords, setPaginatorTotalRecords] = useState<number>(paginationSize);
    const paginatorFirst = Math.max(0, (paginationPage - 1) * paginationSize);
    const ivrConfigurationPermissions = checkPermissions(rolesVision, 'IVRConfig');
    const {
        create: hasIVRConfigurationCreatePermission,
        update: hasIVRConfigurationUpdatePermission,
        delete: hasIVRConfigurationDeletePermission,
    } = ivrConfigurationPermissions;
    const ivrActionsColumnVisible =
        hasIVRConfigurationCreatePermission || hasIVRConfigurationDeletePermission;

    const isActive = [
        {name: 'Активна', value: true},
        {name: 'Не активна', value: false},
    ];

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        dispatch(setIVRConfigurationCurrentPage(1));
        dispatch(setIVRConfigurationCurrentPageSize(10));
    }, []);

    useEffect(() => {
        dispatch(setIVRConfigurationDialogVisible(false));
        return () => {
            dispatch(setIVRConfigurationDialogVisible(false));
        };
    }, [dispatch]);

    useEffect(() => {
        const apiPage = Number(pagination.pageNumber) || 0;
        if (!apiPage) {
            return;
        }
        if (pagination.hasNext) {
            setPaginatorTotalRecords(apiPage * paginationSize + paginationSize);
        } else {
            setPaginatorTotalRecords(
                (apiPage - 1) * paginationSize + ivrConfigurationsList.length
            );
        }
    }, [
        pagination.hasNext,
        pagination.pageNumber,
        paginationSize,
        ivrConfigurationsList.length,
    ]);

    const resetTableHorizontalScroll = () => {
        const scrollableBody = document.querySelector('.p-datatable-wrapper');
        if (scrollableBody) {
            scrollableBody.scrollTop = 0;
            scrollableBody.scrollLeft = 0;
        }
    };

    useEffect(() => {
        const filter: { key: string; value: string }[] = [];
        if (filterFlowId) {
            filter.push(filterFlowId);
        }
        if (filterName) {
            filter.push(filterName);
        }
        if (filterIsActive) {
            filterIsActive.forEach((item) => filter.push(item));
        }
        const dataSort: DataSort = {
            isArchived: false,
            pageNumber: paginationPage,
            pageSize: paginationSize,
            sort: {
                orderBy: orderBy ?? "FlowId",
                isDesc: isDescFilter,
            },
        };
        if (filter.length) {
            dataSort.filter = filter;
        }
        dispatch(getIvrConfigurationsWithPagination(dataSort, jwtToken?.jwtToken));
        setSortData(dataSort);
        resetTableHorizontalScroll();
    }, [
        paginationPage,
        paginationSize,
        orderBy,
        isDescFilter,
        filterFlowId,
        filterName,
        filterIsActive,
    ]);

    useEffect(() => {
        if (
            activeClick?.item !== "/ivrconfiguration" ||
            activeClick?.click !== true
        ) {
            return;
        }
        const fromPath = (activeClick.from ?? "").replace(/^#/, "").split("?")[0];
        const normalized = fromPath.replace(/\/$/, "") || "/";
        if (normalized !== "/ivrconfiguration") {
            return;
        }
        dispatch(getIvrConfigurationsWithPagination(sortData, jwtToken?.jwtToken));
        resetTableHorizontalScroll();
    }, [activeClick, sortData, jwtToken?.jwtToken, dispatch]);

    useEffect(() => {
        setSelectedRowData(null);
    }, [paginationPage, paginationSize]);

    const onPageChange = (event: any) => {
        dispatch(setIVRConfigurationCurrentPage(event.page + 1));
        dispatch(setIVRConfigurationCurrentPageSize(event.rows));
        setSortData((prev: any) => ({...prev, pageNumber: event.page + 1, pageSize: event.rows}));
    };

    const customSort = (event: any) => {
        const handleAction = (sortField: string) => {
            let newOrderBy: string;
            switch (sortField) {
                case 'flowId':
                    newOrderBy = 'FlowId';
                    break;
                case 'name':
                    newOrderBy = 'Name';
                    break;
                case 'isActive':
                    newOrderBy = 'IsActive';
                    break;
                default:
                    return;
            }
            setOrderBy(newOrderBy);
            setIsDescFilter(!isDescFilter);
            setSortOrder(sortOrder === 1 ? -1 : 1);
        };
        handleAction(event.sortField);
        dispatch(setIVRConfigurationCurrentPage(1));
    };

    const customFilter = (event?: any) => {
        if (event) {
            const {flowId, name, isActive} = event.filters;
            const idVal = flowId?.constraints?.[0]?.value;
            setFilterFlowId(
                idVal != null && String(idVal).trim() !== ''
                    ? {key: 'FlowId', value: String(idVal)}
                    : null
            );
            const nameVal = name?.constraints?.[0]?.value;
            setFilterName(
                nameVal != null && String(nameVal).trim() !== ''
                    ? {key: 'Name', value: String(nameVal)}
                    : null
            );
            if (isActive?.value?.length) {
                setFilterIsActive(
                    isActive.value.map((item: boolean) => ({key: 'IsActive', value: String(item)}))
                );
            } else {
                setFilterIsActive(null);
            }
        } else {
            setFilterFlowId(null);
            setFilterName(null);
            setFilterIsActive(null);
            setOrderBy(null);
            setIsDescFilter(true);
        }
    };

    const dataTableRef = useRef<DataTable | null>(null);

    const handleReset = () => {
        dataTableRef?.current?.reset();
        customFilter();
        setSortOrder(0);
        dispatch(setIVRConfigurationCurrentPage(1));
        dispatch(setIVRConfigurationCurrentPageSize(10));
    };

    const isMobile = () => {
        return windowWidth <= 1248;
    };

    const renderHeader = (title: string, sortField: string) => {
        const sorted =
            (sortField === 'flowId' && orderBy === 'FlowId') ||
            (sortField === 'name' && orderBy === 'Name') ||
            (sortField === 'isActive' && orderBy === 'IsActive');
        const sortIcon = classNames('p-sortable-column-icon', {
            'pi pi-sort-amount-up-alt': sorted && sortOrder === 1,
            'pi pi-fw pi-sort-amount-down': sorted && sortOrder === -1,
            'pi pi-fw pi-sort-alt': sortOrder === 0 || !sorted,
        });

        return (
            <div
                className="flex align-items-center sticky top-0"
                onClick={() => customSort({sortField})}
            >
                {title} <i className={sortIcon}></i>
            </div>
        );
    };

    const nameBodyTemplate = (rowData: any) => {
        return (
            <React.Fragment>
                <span style={{marginLeft: '.5em', verticalAlign: 'middle'}} className="image-text">
                    {rowData?.name}
                </span>
            </React.Fragment>
        );
    };

    const toMultiSelectFilterValue = (v: unknown): unknown[] => {
        if (v == null) return [];
        if (Array.isArray(v)) return v;
        return [v];
    };

    const isActiveFilterTemplate = (options: any) => {
        return (
            <>
                <div className="mb-3 text-bold">Оберіть</div>
                <MultiSelect
                    filter
                    value={toMultiSelectFilterValue(options.value)}
                    options={isActive}
                    optionLabel="name"
                    optionValue="value"
                    onChange={(e: any) => options.filterCallback(e.value)}
                    placeholder="нічого не обрано"
                    className="p-column-filter"
                />
            </>
        );
    };

    const filterClearTemplate = (options: any) => {
        return <Button type="button" icon="pi pi-times" onClick={options.filterClearCallback}
                       className="p-button-secondary"></Button>;
    };

    const filterApplyTemplate = (options: any) => {
        return (
            <Button
                type="button"
                icon="pi pi-check"
                onClick={() => {
                    dispatch(setIVRConfigurationCurrentPage(1));
                    options.filterApplyCallback();
                }}
                className=""
            />
        );
    };

    const showDeleteIvrConfigurationConfirm = (flowId: number) => {
        confirmDialog({
            message: 'IVR схема буде видалена. Ви дійсно хочете видалити IVR схему?',
            header: 'Підтвердження',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Так',
            rejectLabel: 'Ні',
            accept: () => {
                if (jwtToken?.jwtToken == null) {
                    return;
                }
                dispatch(deleteIVRConfiguration(flowId, jwtToken.jwtToken));
                if (selectedRowData === flowId) {
                    setSelectedRowData(null);
                }
            },
        });
    };

    const showCopyIvrConfigurationConfirm = (flowId: number) => {
        confirmDialog({
            message: 'Бажаєте скопіювати конфігурацію?',
            header: 'Підтвердження',
            icon: 'pi pi-question-circle',
            acceptLabel: 'Так',
            rejectLabel: 'Ні',
            accept: () => {
                if (jwtToken?.jwtToken == null) {
                    return;
                }
                dispatch(copyIVRConfiguration(flowId, jwtToken.jwtToken));
            },
        });
    };

    const ivrActionTemplate = (rowData: { flowId: number }) => (
        <div className="flex">
            <div className="controls-buttons">
                {hasIVRConfigurationCreatePermission && (
                    <Button
                        type="button"
                        className="p-button-text p-button-icon"
                        icon="pi pi-copy"
                        aria-label="Копіювати конфігурацію"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            showCopyIvrConfigurationConfirm(rowData.flowId);
                        }}
                    />
                )}
            </div>
            {hasIVRConfigurationDeletePermission && (
                <Button
                    type="button"
                    className="p-button-text p-button-icon"
                    icon="pi pi-trash"
                    aria-label="Видалити конфігурацію"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        showDeleteIvrConfigurationConfirm(rowData.flowId);
                    }}
                />
            )}
        </div>
    );

    const openEditIvrDialog = () => {
        if (selectedRowData == null) {
            return;
        }

        dispatch(getIVRConfigurationById(selectedRowData, jwtToken?.jwtToken));
        dispatch(setSelectedIVRConfigurationIndex(selectedRowData));
        dispatch(setIVRConfigurationErrorFields(null));
        dispatch(setIVRConfigurationDialogVisible(true));
        dispatch(setEditType(!!hasIVRConfigurationUpdatePermission));
    };

    return (
        <div className="layout-dashboard">
            <Toast ref={toast}/>
            <div className="">
                <div className="flex col-12 md:col-12 p-0 flex-wrap">
                    <div
                        className={classNames('card widget-table col-12 transition-duration-300 monitoring-table align-self-start m-0 mr-0', {'mb-4': selectedRowData && !isMobile(), 'md:col-12': !selectedRowData})}>
                        <IVRConfigurationListNav handleReset={handleReset} setSelectedRowData={setSelectedRowData}/>
                        <DataTable
                            ref={dataTableRef}
                            scrollable
                            scrollHeight={(selectedRowData) && !isMobile() ? '35vh' : '70vh'}
                            className="p-datatable-customers monitoring-table"
                            value={ivrConfigurationsList}
                            dataKey="flowId"
                            filters={clearFilters}
                            rowsPerPageOptions={[10, 15, 25, 50]}
                            rowHover={false}
                            selectionMode="single"
                            selection={selectedRowData}
                            onFilter={customFilter}
                            emptyMessage="Нічого не знайдено"
                            responsiveLayout='scroll'
                             onRowClick={(e) => {
                                 setSelectedRowData(e.data.flowId);
                                 dispatch(getIVRConfigurationById(e.data.flowId, jwtToken?.jwtToken))
                                 dispatch(setSelectedIVRConfigurationIndex(e.data.flowId));
                                 setMobileVisible(true);
                                 if (isMobile()) {
                                     setMobileVisible(true);
                                 }
                             }}
                            loading={loader}
                            rowClassName={(rowData: any) =>
                                rowData?.flowId === selectedRowData ? 'p-highlight' : ''
                            }
                        >
                            <Column
                                field="flowId"
                                filterField="flowId"
                                header={renderHeader('ID', 'flowId')}
                                showFilterMatchModes={false}
                                showFilterMenuOptions={false}
                                filterPlaceholder="Введіть ID"
                                filter
                                className={classNames('py-3', {'active-header-column': orderBy === 'FlowId'})}
                                body={(data) => data.flowId}
                                filterClear={filterClearTemplate}
                                filterApply={filterApplyTemplate}
                                style={{cursor: 'pointer', width: '10%', minWidth: '90px'}}
                            />
                            <Column
                                field="name"
                                filterField="name"
                                header={renderHeader('Назва', 'name')}
                                className={classNames('', {'active-header-column': orderBy === 'Name'})}
                                showFilterMatchModes={false}
                                showFilterMenuOptions={false}
                                filterPlaceholder="Введіть назву"
                                filter
                                body={nameBodyTemplate}
                                filterClear={filterClearTemplate}
                                filterApply={filterApplyTemplate}
                                style={{cursor: 'pointer', width: '100%', minWidth: '150px'}}
                            />
                            <Column
                                field="isActive"
                                filterField="isActive"
                                header={renderHeader('Стан', 'isActive')}
                                className={classNames('', {'active-header-column': orderBy === 'IsActive'})}
                                showFilterMatchModes={false}
                                showFilterMenuOptions={false}
                                filter
                                body={data => data?.isActive ? 'Активна' : 'Не активна'}
                                filterClear={filterClearTemplate}
                                filterApply={filterApplyTemplate}
                                filterElement={isActiveFilterTemplate}
                                style={{cursor: 'pointer', width: '100%', minWidth: '150px'}}
                            />
                            {ivrActionsColumnVisible && (
                                <Column
                                    header="Дія"
                                    body={ivrActionTemplate}
                                    style={{width: '10%', minWidth: '100px'}}
                                />
                            )}
                        </DataTable>
                        <Paginator
                            first={paginatorFirst}
                            rows={paginationSize}
                            totalRecords={paginatorTotalRecords}
                            rowsPerPageOptions={[5, 10, 15, 25, 50]}
                            onPageChange={onPageChange}
                            className="border-top-1 border-bottom-1"
                            template="PrevPageLink PageLinks NextPageLink RowsPerPageDropdown"
                        />
                    </div>
                    {selectedRowData && (
                        <>
                            {windowWidth > 1248 && <div
                                className={classNames('card widget-table col-12 md:col-12 align-self-start sticky top-0')}>
                                <IVRConfigurationListDetails
                                    setSelectedRowData={setSelectedRowData}
                                    selectedRowData={selectedRowData}
                                    windowWidth={windowWidth}
                                    ivrConfigurationsList={ivrConfigurationsList}
                                />
                            </div>}

                            {windowWidth <= 1248 && <div>
                                <Dialog visible={mobileVisible} closable={false} header={() => {
                                    return <>
                                        <div className="flex align-items-center justify-content-end m-0 p-0 mr-1">
                                            <Button
                                                className="p-dialog-header-icon p-dialog-header-close p-link border-noround outline-none shadow-none h-full justify-content-end"
                                                onClick={() => {
                                                    openEditIvrDialog();
                                                }}>
                                                <span className={classNames('', {
                                                    'pi pi-pencil': hasIVRConfigurationUpdatePermission,
                                                    'pi pi-eye': !hasIVRConfigurationUpdatePermission
                                                })}></span>
                                            </Button>
                                            <Button
                                                className="p-dialog-header-icon p-dialog-header-close p-link border-noround	outline-none shadow-none h-full justify-content-end"
                                                onClick={() => {
                                                    setMobileVisible(false);
                                                }}>
                                                <span className="pi pi-times"></span>
                                            </Button>
                                        </div>
                                    </>
                                }} className="monitoring-info"
                                        onHide={() => setMobileVisible(false)}>
                                    <IVRConfigurationListDetails
                                        setSelectedRowData={setSelectedRowData}
                                        selectedRowData={selectedRowData}
                                        ivrConfigurationsList={ivrConfigurationsList}
                                    />
                                </Dialog>
                            </div>}
                        </>
                    )}
                    <IVRConfigurationDialog setSelectedRowData={setSelectedRowData}/>
                    <ConfirmDialog />
                </div>
            </div>
        </div>
    );
};

export default IVRConfigurationList;
